import { useState, useEffect, useMemo } from 'react';
import { Reservation } from '../types';
import { PaymentRecord } from './paymentService';
import {
  getReservationModificationLogs,
  ReservationModificationLog,
  getReservationHistory,
  ApiReservation,
  ReservationVersion,
} from './reservationService';

export interface UseModificationHistoryResult {
  isModifiedReservation: boolean;
  modLogs: ReservationModificationLog[];
  modLogsLoading: boolean;
  oldReservationMap: Record<number, ApiReservation>;
  modLogsExpanded: boolean;
  setModLogsExpanded: (expanded: boolean) => void;
  paymentsByTransactionId: Map<string, PaymentRecord>;
}

/**
 * Convert a ReservationVersion from the history endpoint to an ApiReservation-like shape
 * so the existing ModificationHistorySection can display old reservation data.
 */
function versionToApiReservation(version: ReservationVersion): ApiReservation {
  return {
    id: version.id,
    store_id: 0, // Not provided by history endpoint
    check_in: version.check_in || '',
    days: version.days,
    adult: version.sites.reduce((sum, s) => sum + s.adults, 0),
    child: version.sites.reduce((sum, s) => sum + s.children, 0),
    pet: version.sites.reduce((sum, s) => sum + s.pets, 0),
    vehicle: version.sites.reduce((sum, s) => sum + s.vehicles, 0),
    cost: version.cost,
    status: version.status,
    active: version.active,
    is_current_version: version.is_current_version,
    reservation_version: version.version,
    supersedes_reservation_id: version.supersedes_reservation_id ?? undefined,
    superseded_by_reservation_id: version.superseded_by_reservation_id ?? undefined,
    created_at: version.created_at ?? undefined,
    reservation_sites: version.sites.map((s) => ({
      reservation_id: version.id,
      site_id: s.site_id ?? 0,
      site_cost: s.site_cost,
      adults: s.adults,
      children: s.children,
      pets: s.pets,
      vehicles: s.vehicles,
      status: 'active',
      site: {
        id: s.site_id ?? 0,
        alias: s.alias,
      },
    })),
  };
}

export function useModificationHistory(
  reservation: Reservation | null,
  payments: PaymentRecord[]
): UseModificationHistoryResult {
  const [modLogs, setModLogs] = useState<ReservationModificationLog[]>([]);
  const [modLogsLoading, setModLogsLoading] = useState(false);
  const [modLogsExpanded, setModLogsExpanded] = useState(false);
  const [oldReservationMap, setOldReservationMap] = useState<Record<number, ApiReservation>>({});

  const isModifiedReservation = Boolean(
    reservation &&
    reservation.isCurrentVersion === true &&
    reservation.originalReservationId != null &&
    reservation.supersedesReservationId != null
  );

  // Reset state when reservation changes
  useEffect(() => {
    if (!reservation) {
      setModLogs([]);
      setOldReservationMap({});
      setModLogsExpanded(false);
    }
  }, [reservation]);

  // Fetch mod logs + reservation history in parallel (2 calls instead of N+1)
  useEffect(() => {
    if (!reservation || !isModifiedReservation) return;

    const originalId = reservation.originalReservationId!;
    const confirmationCode = reservation.confirmationCode;

    if (!confirmationCode) return;

    setModLogsLoading(true);

    Promise.all([
      getReservationModificationLogs(originalId),
      getReservationHistory(confirmationCode),
    ])
      .then(([logs, history]) => {
        // Sort logs by date descending
        const sorted = [...logs].sort((a, b) => {
          const dateA = a.modified_at || a.created_at || '';
          const dateB = b.modified_at || b.created_at || '';
          return dateB.localeCompare(dateA);
        });
        setModLogs(sorted);

        // Build oldReservationMap from history versions
        const resMap: Record<number, ApiReservation> = {};
        for (const version of history.versions) {
          resMap[version.id] = versionToApiReservation(version);
        }
        setOldReservationMap(resMap);
      })
      .catch((err) => console.error('Modification history fetch error:', err))
      .finally(() => setModLogsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation?.id, reservation?.originalReservationId, reservation?.confirmationCode, isModifiedReservation]);

  const paymentsByTransactionId = useMemo(() => {
    const map = new Map<string, PaymentRecord>();
    for (const p of payments) {
      if (p.transaction_id) {
        map.set(p.transaction_id, p);
      }
    }
    return map;
  }, [payments]);

  return {
    isModifiedReservation,
    modLogs,
    modLogsLoading,
    oldReservationMap,
    modLogsExpanded,
    setModLogsExpanded,
    paymentsByTransactionId,
  };
}
